import { storage as localStorage } from './storage';
import { db, isFirebaseConfigured } from './firebaseConfig';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { Page, MenuItem, BlogPost, HeroContent, BrandingConfig } from '../types';

// Interfaces moved to types/index.ts

const KEYS = {
    HERO: 'sbaj_hero_content',
    BLOG: 'sbaj_blog_posts',
    PAGES: 'sbaj_custom_pages',
    MENU: 'sbaj_menu_items',
    BRANDING: 'sbaj_branding_config'
};

const DEFAULT_MENU_ITEMS: MenuItem[] = [
    { id: '1', label: 'Home', path: '/', order: 0, isVisible: true },
    { id: '2', label: 'Training', path: '/training', order: 1, isVisible: true },
    { id: '3', label: 'Resources', path: '/resources', order: 2, isVisible: true },
    { id: '4', label: 'Threats', path: '/threats', order: 3, isVisible: true },
    { id: '5', label: 'Blog', path: '/blog', order: 4, isVisible: true },
    { id: '6', label: 'Forum', path: '/forum', order: 5, isVisible: true }
];

const DEFAULT_HERO_CONTENT: Record<string, HeroContent> = {
    home: {
        type: 'static',
        title: 'Protect Your Business',
        subtitle: 'Comprehensive cybersecurity training and threat intelligence for Jamaican SMEs',
        ctaText: 'Get Started',
        items: [{ id: '1', url: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b', type: 'image' }],
        overlayEnabled: true,
        overlayColor: '#000000',
        overlayOpacity: 0.5
    },
    training: {
        type: 'static',
        title: 'Cybersecurity Training',
        subtitle: 'Empower your team with the knowledge to defend against cyber threats.',
        ctaText: 'Start Learning',
        items: [{ id: '1', url: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3', type: 'image' }],
        overlayEnabled: true,
        overlayColor: '#000000',
        overlayOpacity: 0.6
    },
    resources: {
        type: 'static',
        title: 'Cybersecurity Resources',
        subtitle: 'Professional tools, templates, and guides to secure your business.',
        ctaText: 'Browse Resources',
        items: [{ id: '1', url: 'https://images.unsplash.com/photo-1563986768609-322da13575f3', type: 'image' }],
        overlayEnabled: true,
        overlayColor: '#000000',
        overlayOpacity: 0.6
    },
    threats: {
        type: 'static',
        title: 'Current Threat Intelligence',
        subtitle: 'Stay informed about the latest cyber threats targeting Jamaican businesses.',
        ctaText: 'View All Threats',
        items: [{ id: '1', url: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb', type: 'image' }],
        overlayEnabled: true,
        overlayColor: '#000000',
        overlayOpacity: 0.6
    }
};

export const DEFAULT_BLOG_POSTS: BlogPost[] = [
    {
        id: '1',
        title: 'The Rise of Phishing Attacks in 2024',
        excerpt: 'Learn how to identify and protect your business from sophisticated phishing attempts.',
        content: 'Phishing attacks are becoming increasingly sophisticated...',
        author: 'SBAJ Security Team',
        date: '2023-10-25',
        imageUrl: 'https://images.unsplash.com/photo-1563986768609-322da13575f3',
        category: 'Threats',
        status: 'published'
    },
    {
        id: '2',
        title: 'Data Protection Act Compliance Guide',
        excerpt: 'Essential steps for Jamaican SMEs to ensure compliance with the new Data Protection Act.',
        content: 'The Data Protection Act requires businesses to...',
        author: 'Legal Dept',
        date: '2023-11-12',
        imageUrl: 'https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d',
        category: 'Compliance',
        status: 'published'
    },
    {
        id: '3',
        title: 'Securing Remote Workforces',
        excerpt: 'Best practices for maintaining security while your team works from home.',
        content: 'Remote work introduces new security challenges...',
        author: 'SBAJ Tech',
        date: '2023-11-28',
        imageUrl: 'https://images.unsplash.com/photo-1593642632823-8f78536788c6',
        category: 'Best Practices',
        status: 'published'
    }
];

export const contentService = {
    getHeroContent: async (pageId: string = 'home'): Promise<HeroContent> => {
        const defaultContent = DEFAULT_HERO_CONTENT[pageId] || DEFAULT_HERO_CONTENT['home'];

        if (isFirebaseConfigured() && db) {
            try {
                const docRef = doc(db, 'content', `hero_${pageId}`);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    return docSnap.data() as HeroContent;
                } else {
                    // Initialize if not exists
                    await setDoc(docRef, defaultContent);
                    return defaultContent;
                }
            } catch (error) {
                console.error(`Error fetching hero content for ${pageId} from Firebase:`, error);
                return defaultContent;
            }
        }

        // Mock Fallback
        await localStorage.delay(200);
        return localStorage.get<HeroContent>(`${KEYS.HERO}_${pageId}`, defaultContent);
    },

    updateHeroContent: async (pageId: string, content: HeroContent): Promise<HeroContent> => {
        const updatedContent = { ...content, lastUpdated: Date.now() };

        if (isFirebaseConfigured() && db) {
            try {
                await setDoc(doc(db, 'content', `hero_${pageId}`), updatedContent);
                return updatedContent;
            } catch (error) {
                console.warn(`Error updating hero content for ${pageId} in Firebase, falling back to mock:`, error);
                // Fallthrough to mock
            }
        }

        // Mock Fallback
        await localStorage.delay(500);
        localStorage.set(`${KEYS.HERO}_${pageId}`, updatedContent);
        return updatedContent;
    },

    getBlogPosts: async (): Promise<BlogPost[]> => {
        if (isFirebaseConfigured() && db) {
            try {
                const querySnapshot = await getDocs(collection(db, 'blog_posts'));
                const posts = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as BlogPost));

                if (posts.length === 0) {
                    // Seed default posts
                    for (const post of DEFAULT_BLOG_POSTS) {
                        await setDoc(doc(db, 'blog_posts', post.id), post);
                    }
                    return DEFAULT_BLOG_POSTS;
                }
                return posts;
            } catch (error) {
                console.warn('Error fetching blog posts from Firebase, falling back to mock:', error);
                // Fallthrough to mock
            }
        }

        // Mock Fallback
        await localStorage.delay(300);
        return localStorage.get<BlogPost[]>(KEYS.BLOG, DEFAULT_BLOG_POSTS);
    },

    updateBlogPost: async (post: BlogPost): Promise<BlogPost> => {
        if (isFirebaseConfigured() && db) {
            try {
                await setDoc(doc(db, 'blog_posts', post.id), post);
                return post;
            } catch (error) {
                console.warn('Error updating blog post in Firebase, falling back to mock:', error);
                // Fallthrough to mock
            }
        }

        // Mock Fallback
        await localStorage.delay(400);
        const posts = await contentService.getBlogPosts();
        const updatedPosts = posts.map(p => p.id === post.id ? post : p);
        localStorage.set(KEYS.BLOG, updatedPosts);
        return post;
    },

    addBlogPost: async (post: Omit<BlogPost, 'id'>): Promise<BlogPost> => {
        // Send email notification to all users (simulated)
        const { emailService } = await import('./emailService');
        emailService.sendNewBlogPostEmail('all-users@sbaj.org.jm', post.title, post.excerpt);

        if (isFirebaseConfigured() && db) {
            try {
                const docRef = await addDoc(collection(db, 'blog_posts'), post);
                return { id: docRef.id, ...post } as BlogPost;
            } catch (error) {
                console.warn('Error adding blog post to Firebase, falling back to mock:', error);
                // Fallthrough to mock
            }
        }

        // Mock Fallback
        await localStorage.delay(400);
        const posts = await contentService.getBlogPosts();
        const newPost = { ...post, id: Date.now().toString() };
        localStorage.set(KEYS.BLOG, [...posts, newPost]);
        return newPost;
    },

    deleteBlogPost: async (id: string): Promise<void> => {
        if (isFirebaseConfigured() && db) {
            try {
                await deleteDoc(doc(db, 'blog_posts', id));
                return;
            } catch (error) {
                console.warn('Error deleting blog post from Firebase, falling back to mock:', error);
                // Fallthrough to mock
            }
        }

        // Mock Fallback
        await localStorage.delay(300);
        const posts = await contentService.getBlogPosts();
        localStorage.set(KEYS.BLOG, posts.filter(p => p.id !== id));
    },

    // --- Dynamic Pages ---
    getPages: async (): Promise<Page[]> => {
        if (isFirebaseConfigured() && db) {
            try {
                const querySnapshot = await getDocs(collection(db, 'pages'));
                return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Page));
            } catch (error) {
                console.warn('Error fetching pages from Firebase, falling back to mock:', error);
                // Fallthrough to mock
            }
        }
        await localStorage.delay(200);
        return localStorage.get<Page[]>(KEYS.PAGES, []);
    },

    getPage: async (slug: string): Promise<Page | null> => {
        const pages = await contentService.getPages();
        return pages.find(p => p.slug === slug && p.status === 'published') || null;
    },

    savePage: async (page: Page): Promise<Page> => {
        const updatedPage = { ...page, lastUpdated: Date.now() };
        if (isFirebaseConfigured() && db) {
            try {
                await setDoc(doc(db, 'pages', page.id), updatedPage);
                return updatedPage;
            } catch (error) {
                console.warn('Error saving page to Firebase, falling back to mock:', error);
                // Fallthrough to mock
            }
        }
        await localStorage.delay(400);
        const pages = await contentService.getPages();
        const existingIndex = pages.findIndex(p => p.id === page.id);
        const newPages = existingIndex >= 0
            ? pages.map(p => p.id === page.id ? updatedPage : p)
            : [...pages, updatedPage];
        localStorage.set(KEYS.PAGES, newPages);
        return updatedPage;
    },

    deletePage: async (id: string): Promise<void> => {
        if (isFirebaseConfigured() && db) {
            try {
                await deleteDoc(doc(db, 'pages', id));
                return;
            } catch (error) {
                console.warn('Error deleting page from Firebase, falling back to mock:', error);
                // Fallthrough to mock
            }
        }
        await localStorage.delay(300);
        const pages = await contentService.getPages();
        localStorage.set(KEYS.PAGES, pages.filter(p => p.id !== id));
    },

    // --- Menu Management ---
    getMenuItems: async (): Promise<MenuItem[]> => {
        if (isFirebaseConfigured() && db) {
            try {
                const querySnapshot = await getDocs(collection(db, 'menu_items'));
                const items = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MenuItem));

                if (items.length === 0) {
                    // Seed default menu
                    for (const item of DEFAULT_MENU_ITEMS) {
                        await setDoc(doc(db, 'menu_items', item.id), item);
                    }
                    return DEFAULT_MENU_ITEMS;
                }

                return items.sort((a, b) => a.order - b.order);
            } catch (error) {
                console.warn('Error fetching menu items from Firebase, falling back to mock:', error);
                // Fallthrough to mock
            }
        }
        await localStorage.delay(200);
        return localStorage.get<MenuItem[]>(KEYS.MENU, DEFAULT_MENU_ITEMS).sort((a, b) => a.order - b.order);
    },

    saveMenuItems: async (items: MenuItem[]): Promise<MenuItem[]> => {
        if (isFirebaseConfigured() && db) {
            try {
                // Batch update or just individual sets
                for (const item of items) {
                    await setDoc(doc(db, 'menu_items', item.id), item);
                }
                // Handle deletions if necessary (omitted for simplicity, assuming full replace logic elsewhere)
                return items;
            } catch (error) {
                console.warn('Error saving menu items to Firebase, falling back to mock:', error);
                // Fallthrough to mock
            }
        }
        await localStorage.delay(400);
        localStorage.set(KEYS.MENU, items);
        return items;
    },

    // --- Branding ---
    getBranding: async (): Promise<BrandingConfig> => {
        const defaultBranding: BrandingConfig = {
            siteTitle: 'SBAJ Cybersecurity',
        };

        if (isFirebaseConfigured() && db) {
            try {
                const docRef = doc(db, 'content', 'branding');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    return docSnap.data() as BrandingConfig;
                }
                return defaultBranding;
            } catch (error) {
                console.warn('Error fetching branding from Firebase:', error);
            }
        }
        await localStorage.delay(200);
        return localStorage.get<BrandingConfig>(KEYS.BRANDING, defaultBranding);
    },

    saveBranding: async (config: BrandingConfig): Promise<BrandingConfig> => {
        if (isFirebaseConfigured() && db) {
            try {
                await setDoc(doc(db, 'content', 'branding'), config);
                return config;
            } catch (error) {
                console.warn('Error saving branding to Firebase:', error);
            }
        }
        await localStorage.delay(400);
        localStorage.set(KEYS.BRANDING, config);
        return config;
    }
};
